import * as React from "react";
import { Survey, surveyHelper } from "../../../../../../../amorg-common";
import { Button } from "../../../../../../../designSystem/button/Button";
import { Flex } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";
import { Modal } from "../../../../../../../designSystem/Modal";
import { surveyQuestionHelper } from "../../../../../../../amorg-common/helpers/surveyQuestionHelper";
import { ToggleChip } from "../../../../../../../designSystem/Chip";
import { updateSurveyQuestion } from "../../../../../../../api/surveyClientApi";
import { OnOff } from "../../../../../../../designSystem/inputs/OnOff";
import { colors } from "../../../../../../../designSystem/colors/color";
import { Small } from "../../../../../../../designSystem/typography/Body";
import { Card } from "../../../../../../../designSystem/Card";

type EditorDraft = {
    conditional: boolean,
    triggerValues: string[],
}

type QuestionConditionsModalProps =
{
    survey: Survey,
    questionIdentifier?: { themeIndex: number, questionIndex: number }
    onQuestionUpdated: () => void,
    onClose: () => void
};

export function QuestionConditionsModal(props: QuestionConditionsModalProps): JSX.Element
{
    const [draft, setDraft] = React.useState<EditorDraft>({ conditional: false, triggerValues: [] });

    if(!props.questionIdentifier)
        return <></>;

    const theme = props.survey.themes[props.questionIdentifier.themeIndex];
    
    const lastUnconditionalQuestion = surveyHelper.findLastUnconditionalQuestion(theme.questions, props.questionIdentifier.questionIndex-1);
    if(!lastUnconditionalQuestion)
        return <></>;

    const question = theme.questions[props.questionIdentifier.questionIndex];
    if(!question)
        return <></>;

    if(surveyQuestionHelper.isScoredQuestion(question))
        return <></>;

    React.useEffect(() => {
        if(props.questionIdentifier)
        {
            const q = props.survey.themes[props.questionIdentifier.themeIndex].questions[props.questionIdentifier.questionIndex];
            setDraft({
                conditional: q.conditionalDisplay !== undefined,
                triggerValues: q.conditionalDisplay?.triggerValues || []
            });
        }
        else {
            setDraft({ conditional: false, triggerValues: [] });
        }
    }, [props.questionIdentifier]);

    const conditionalValues = surveyQuestionHelper.getOptions(lastUnconditionalQuestion);

    function toggleConditionalValue(value: string) {
        if(!draft) return;
        if(draft.triggerValues.includes(value)) {
            setDraft({
                ...draft,
                triggerValues: draft.triggerValues.filter(v => v !== value)
            });
        } else {
            setDraft({
                ...draft,
                triggerValues: [...draft.triggerValues, value]
            });
        }
    }

    async function handleSave() {
        if(!draft) return;
        if(!props.questionIdentifier) return;

        updateSurveyQuestion(
            props.survey.id,
            props.questionIdentifier.themeIndex,
            props.questionIdentifier.questionIndex,
            question.indicators,
            question.title,
            question.translations,
            question.canComment,
            surveyQuestionHelper.getSurveyQuestionConfig(question),
            question.conditionalDisplay = draft.conditional
                ? { triggerValues: draft.triggerValues }
                : undefined
        );
        props.onQuestionUpdated();
    }

    return (
        <Modal
            showModal={props.questionIdentifier !== undefined}
            title="Redigering af visning"
            onCancel={props.onClose}
            content={
                <Flex flexDirection="column" gap={16}>

                    <Card variant="lightGray">
                        {draft?.conditional 
                            ? "Spørgsmålet vises kun, hvis det forrige spørgsmål har et bestemte svar" 
                            : "Spørgsmålet vises altid."
                        }
                    </Card>

                    <Flex gap={8}>
                        <OnOff active={draft?.conditional || false}
                            onClick={(active) => setDraft({ conditional: active, triggerValues: [] })} />
                        <span style={{ color: draft?.conditional ? colors.text.normal : colors.text.muted}}>
                            <Small>Begræns visningen baseret på forrige spørgsmåls svar</Small>
                        </span>
                    </Flex>

                    <Flex gap={4} flexWrap="wrap">
                        {conditionalValues.map((value, index) => (
                            <ToggleChip key={index} 
                                disabled={!draft?.conditional}
                                selected={draft?.triggerValues?.includes(value.value) || false}
                                onClick={() => toggleConditionalValue(value.value)}>{value.text}</ToggleChip>
                        ))}
                    </Flex>

                    <Flex justifyContent="space-between">
                        <Button variant="text" onClick={props.onClose} text="Annuller" />
                        <Button variant="filled" onClick={handleSave} text="Gem" />
                    </Flex>
                </Flex>
            }
            />
    );
}
